import { usePlaylistSearchManagerOpenState } from '../playlistItemsHooks';
import { useMediaQuery } from 'academy-common-lib/utils/Styling';
import { TFlex } from 'foundations-components/transitional/components/TFlex';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { PlaylistDragProvider, PlaylistItem } from './PlaylistItem';
import { PlaylistItemManager, usePlaylistItemManager } from './PlaylistItemManager';
import { CancelPlaylistSearchButton, PlaylistItemSearch, PlaylistItemSearchManager, PlaylistItemSearchProvider, SavePlaylistSearchButton, usePlaylistItemSearch } from './PlaylistItemSearchManager';
import { PlaylistItemSearchResults } from './PlaylistItemSearchResults';
import { Grid, GridItem } from 'foundations-components/transitional/components/Grid';
// @ts-expect-error no types
import TracksTopFiltersContainer from '../../../containers/TracksPage/TracksTopFiltersContainer';
import { useRef } from 'react';
import { PlaylistMobileFilterButton } from './PlaylistMobileFilter';
import { useCanUserEditPlaylist } from '../../savedItems/savedItemsHooks';
import { useLearningContentCardModels } from 'academy-common-lib/features/cards/hooks/useLearningContentCardModels';
import ApiError from '../../../components/Errors/ApiError';
import { getPlaylistItemStatus } from '../playlistItemsUtils';
import { Text } from 'foundations-components/components/Text';
import { StickyFooter } from 'foundations-components/transitional/components/StickyFooter';
import { View } from 'foundations-components/transitional/components/View';
import { List, ListItem } from 'foundations-components/transitional/components/List';
import { ButtonWrapper } from 'foundations-components/transitional/components/ButtonWrapper';
import { useIsPartner } from '../../partners';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
const hasAccessToPlaylistItem = (playlistItem, isPartner) => {
  return playlistItem.visibility === 'PUBLIC' || playlistItem.visibility === 'PARTNER' && isPartner || playlistItem.visibility === 'PARTNER_TYPES_SALES' && isPartner;
};
const LessonPlaylistItem = ({
  playlistItem,
  playlistId,
  position,
  cardModel,
  isLoadingCardModel,
  playlistLength
}) => {
  const isPartner = useIsPartner();
  if (!cardModel && playlistItem.visibility === 'PUBLIC') return null;
  const status = getPlaylistItemStatus(cardModel);
  const accessDenied = !hasAccessToPlaylistItem(playlistItem, isPartner);
  return /*#__PURE__*/_jsx(PlaylistItem, {
    accessDenied: accessDenied,
    cardModel: cardModel,
    "data-test-id": "playlist-item",
    "data-test-item-id": playlistItem.itemId,
    "data-test-item-type": "LESSON",
    isLoadingCardModel: isLoadingCardModel,
    playlistId: playlistId,
    playlistItem: playlistItem,
    playlistLength: playlistLength,
    position: position,
    status: status
  });
};
const TrackPlaylistItem = ({
  playlistItem,
  playlistId,
  position,
  cardModel,
  isLoadingCardModel,
  playlistLength
}) => {
  const isPartner = useIsPartner();
  if (!cardModel) return null;
  const status = getPlaylistItemStatus(cardModel);
  const accessDenied = !hasAccessToPlaylistItem(playlistItem, isPartner);
  return /*#__PURE__*/_jsx(PlaylistItem, {
    accessDenied: accessDenied,
    cardModel: cardModel,
    "data-test-id": "playlist-item",
    "data-test-item-id": playlistItem.itemId,
    "data-test-item-type": "TRACK",
    isLoadingCardModel: isLoadingCardModel,
    playlistId: playlistId,
    playlistItem: playlistItem,
    playlistLength: playlistLength,
    position: position,
    status: status
  });
};
const ShortPlaylistItem = ({
  playlistItem,
  playlistId,
  position,
  cardModel,
  isLoadingCardModel,
  playlistLength
}) => {
  const isPartner = useIsPartner();
  if (!cardModel && playlistItem.visibility === 'PUBLIC') return null;
  const status = getPlaylistItemStatus(cardModel);
  const accessDenied = !hasAccessToPlaylistItem(playlistItem, isPartner);
  return /*#__PURE__*/_jsx(PlaylistItem, {
    accessDenied: accessDenied,
    cardModel: cardModel,
    "data-test-id": "playlist-item",
    "data-test-item-id": playlistItem.itemId,
    "data-test-item-type": "SHORT",
    isLoadingCardModel: isLoadingCardModel,
    playlistId: playlistId,
    playlistItem: playlistItem,
    playlistLength: playlistLength,
    position: position,
    status: status
  });
};
const PlaylistItemCount = () => {
  const {
    numberOfSelectedItems: selectedExistingPlaylistItems
  } = usePlaylistItemManager();
  const {
    numberOfSelectedItems: selectedNewPlaylistItems
  } = usePlaylistItemSearch();
  const count = selectedNewPlaylistItems > 0 ? selectedNewPlaylistItems : selectedExistingPlaylistItems;
  return /*#__PURE__*/_jsx(FormattedMessage, {
    message: "learningCenter.savedItems.playlistDetails.numberOfPlaylistItems",
    options: {
      count
    }
  });
};
const PlaylistItemSearchManagerList = ({
  playlistId,
  playlistItems
}) => {
  // retrieves hydrated card models for the elements to render
  const {
    cardModels,
    cardModelsError,
    cardModelsLoading,
    userProgressDataLoading
  } = useLearningContentCardModels({
    items: playlistItems.map(({
      itemId,
      type,
      language
    }) => {
      return {
        id: itemId,
        type: type,
        language: language
      };
    }) || []
  });
  if (cardModelsError) return /*#__PURE__*/_jsx(ApiError, {
    error: cardModelsError
  });
  return /*#__PURE__*/_jsx(PlaylistDragProvider, {
    children: /*#__PURE__*/_jsx(View, {
      marginBlockStart: "500",
      children: /*#__PURE__*/_jsx(List, {
        "data-test-id": "playlist-items-list",
        ordered: true,
        children: playlistItems.map((playlistItem, i) => {
          let Item = null;
          const cardModel = cardModels === null || cardModels === void 0 ? void 0 : cardModels.find(({
            id,
            type,
            language
          }) => id === playlistItem.itemId && type === playlistItem.type && language.toLowerCase() === playlistItem.language);
          switch (playlistItem.type) {
            case 'LESSON':
              Item = LessonPlaylistItem;
              break;
            case 'TRACK':
              Item = TrackPlaylistItem;
              break;
            case 'SHORT_FORM_CONTENT':
              Item = ShortPlaylistItem;
              break;
            default:
              return null;
          }
          return /*#__PURE__*/_jsx(ListItem, {
            children: /*#__PURE__*/_jsx(Item, {
              cardModel: cardModel,
              isLoadingCardModel: cardModelsLoading || userProgressDataLoading,
              playlistId: playlistId,
              playlistItem: playlistItem,
              playlistLength: playlistItems.length,
              position: i
            })
          }, playlistItem.itemId);
        })
      })
    })
  });
};
export const PlaylistItems = ({
  playlistId,
  searchInputRef
}) => {
  const {
    isMobile
  } = useMediaQuery();
  const userCanEditPlaylist = useCanUserEditPlaylist(playlistId);
  const scrollRef = useRef(null);
  const {
    isPlaylistSearchManagerOpen
  } = usePlaylistSearchManagerOpenState();
  const handleOnSearchPageChange = () => {
    if (scrollRef.current) {
      scrollRef.current.scrollIntoView();
    }
  };
  return /*#__PURE__*/_jsx(PlaylistItemManager, {
    playlistId: playlistId,
    render: playlistItems => {
      return /*#__PURE__*/_jsxs(_Fragment, {
        children: [/*#__PURE__*/_jsx("div", {
          ref: scrollRef,
          children: /*#__PURE__*/_jsx(Text, {
            as: isMobile ? 'h5' : 'h4',
            marginBlockEnd: "100",
            children: /*#__PURE__*/_jsx(FormattedMessage, {
              message: "learningCenter.savedItems.playlistDetails.playlistItems"
            })
          })
        }), /*#__PURE__*/_jsxs(PlaylistItemSearchProvider, {
          playlistId: playlistId,
          children: [userCanEditPlaylist && /*#__PURE__*/_jsxs(_Fragment, {
            children: [/*#__PURE__*/_jsxs(TFlex, {
              align: "center",
              justify: "between",
              children: [/*#__PURE__*/_jsxs(List, {
                marginBlockEnd: "200",
                use: "inline-divided",
                children: [/*#__PURE__*/_jsx(ListItem, {
                  children: /*#__PURE__*/_jsx(PlaylistItemCount, {})
                }), !isPlaylistSearchManagerOpen && /*#__PURE__*/_jsx(ListItem, {
                  children: /*#__PURE__*/_jsx(FormattedMessage, {
                    message: "learningCenter.savedItems.playlistDetails.dragToReorder"
                  })
                }), !isMobile && !isPlaylistSearchManagerOpen && /*#__PURE__*/_jsx(ListItem, {
                  children: /*#__PURE__*/_jsx(FormattedMessage, {
                    message: "learningCenter.savedItems.playlistDetails.deselectToRemove"
                  })
                })]
              }), isMobile && /*#__PURE__*/_jsx(PlaylistMobileFilterButton, {})]
            }), /*#__PURE__*/_jsxs(Grid, {
              children: [/*#__PURE__*/_jsx(GridItem, {
                size: {
                  xs: 12,
                  md: 6
                },
                children: /*#__PURE__*/_jsx(PlaylistItemSearch, {
                  inputRef: searchInputRef
                })
              }), /*#__PURE__*/_jsx(GridItem, {
                size: {
                  xs: 12,
                  md: 6
                },
                children: /*#__PURE__*/_jsx(PlaylistItemSearchManager, {
                  searchView: isMobile ? /*#__PURE__*/_jsx(StickyFooter, {
                    children: /*#__PURE__*/_jsx(View, {
                      backgroundColor: "fill-surface-raised",
                      paddingBlockEnd: "500",
                      paddingBlockStart: "400",
                      paddingInline: "200",
                      children: /*#__PURE__*/_jsxs(Grid, {
                        children: [/*#__PURE__*/_jsx(GridItem, {
                          size: 6,
                          children: /*#__PURE__*/_jsx(CancelPlaylistSearchButton, {})
                        }), /*#__PURE__*/_jsx(GridItem, {
                          size: 6,
                          children: /*#__PURE__*/_jsx(SavePlaylistSearchButton, {})
                        })]
                      })
                    })
                  }) : /*#__PURE__*/_jsx(TFlex, {
                    justify: "end",
                    children: /*#__PURE__*/_jsxs(ButtonWrapper, {
                      children: [/*#__PURE__*/_jsx(CancelPlaylistSearchButton, {}), /*#__PURE__*/_jsx(SavePlaylistSearchButton, {})]
                    })
                  })
                })
              })]
            })]
          }), /*#__PURE__*/_jsx(PlaylistItemSearchManager, {
            listView: playlistItems.length && /*#__PURE__*/_jsx(PlaylistItemSearchManagerList, {
              playlistId: playlistId,
              playlistItems: playlistItems
            }),
            searchView: /*#__PURE__*/_jsxs(_Fragment, {
              children: [!isMobile && /*#__PURE__*/_jsx(TracksTopFiltersContainer, {}), /*#__PURE__*/_jsx(PlaylistItemSearchResults, {
                onSearchPageChange: handleOnSearchPageChange
              })]
            })
          })]
        })]
      });
    }
  });
};